import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@store/hooks';
import { selectLiveSummary } from '@store/selectors/monitor.selectors';
import { UsersIcon } from '@utils/icons';
import { useReplayViewContext } from '../replay/ReplayViewContext';
import styles from './leftRail.module.css';

type Tile = { key: string; value: number | undefined; labelKey: string };

/**
 * Headline counts card at the top of the LeftRail card stack. Non-collapsible
 * — reuses the `.section` card shell; the 2×2 grid mirrors the reference
 * mockup. Live values come from `selectLiveSummary` (derived, no new wire data);
 * in replay they are the frame's (held through data gaps), and `—` before the
 * day's first frame.
 */
export function LiveSummaryCard() {
  const { t } = useTranslation();
  const live = useAppSelector(selectLiveSummary);
  const replay = useReplayViewContext();
  const summary = replay ? replay.summary : live;

  const tiles: Tile[] = [
    {
      key: 'workers',
      value: summary?.workersOnSite,
      labelKey: 'monitor.liveSummary.workersOnSite',
    },
    {
      key: 'floors',
      value: summary?.activeFloors,
      labelKey: 'monitor.liveSummary.activeFloors',
    },
    {
      key: 'managers',
      value: summary?.managers,
      labelKey: 'monitor.liveSummary.managers',
    },
    {
      key: 'trades',
      value: summary?.activeTrades,
      labelKey: 'monitor.liveSummary.activeTrades',
    },
  ];

  return (
    <section className={styles.section}>
      <div className={styles.cardHeader}>
        <span className={styles.cardHeaderIcon}>
          <UsersIcon size={16} />
        </span>
        <span className={styles.cardHeaderTitle}>
          {t(replay ? 'monitor.liveSummary.titleReplay' : 'monitor.liveSummary.title')}
        </span>
      </div>
      <div className={styles.summaryGrid}>
        {tiles.map((tile) => (
          <div key={tile.key} className={styles.summaryTile}>
            <span className={styles.summaryValue}>{tile.value ?? '—'}</span>
            <span className={styles.summaryLabel}>{t(tile.labelKey)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
